// Types for the contractor agreement form and API

import { z } from "zod";

// Zod schema for validation
export const contractorFormSchema = z.object({
  // Parties
  client_full_legal_name: z
    .string()
    .min(2, "Name must be at least 2 characters"),
  client_address: z.string().min(5, "Please enter a valid address"),
  contractor_full_legal_name: z
    .string()
    .min(2, "Name must be at least 2 characters"),
  contractor_address: z.string().min(5, "Please enter a valid address"),
  contractor_business_name: z.string().optional(),

  // Services
  schedule_a_services_description: z
    .string()
    .min(10, "Please describe the services in detail"),

  // Fees & Invoicing
  fee_currency_symbol: z.string().default("£"),
  fee_rate: z.number().min(1, "Fee rate must be greater than 0"),
  fee_basis: z.string().min(1, "Please specify how the fee is charged"),
  invoice_frequency: z
    .string()
    .min(1, "Please specify how often invoices are sent"),
  payment_due_days: z.number().min(1, "Payment due days must be at least 1"),
  schedule_b_fees_description: z.string().optional(),

  // Safeguarding & Background Checks
  background_check_examples: z
    .string()
    .min(1, "Please specify required background checks"),
  schedule_c_safeguarding_description: z.string().optional(),

  // Confidentiality & Data Protection
  data_protection_regulation: z
    .string()
    .min(1, "Please specify applicable data protection regulation"),

  // Insurance & Liability
  has_insurance: z.boolean(),
  insurance_currency_symbol: z.string().optional(),
  insurance_cover_amount: z.number().optional(),
  liability_cap_currency_symbol: z.string().optional(),
  liability_cap_amount: z.number().optional(),

  // Term & Termination
  start_date: z.string().min(1, "Start date is required"),
  end_date_or_completion: z
    .string()
    .min(1, "Please specify end date or completion criteria"),
  termination_notice_days: z
    .number()
    .min(1, "Termination notice days must be at least 1"),

  // Non-Solicitation (Optional)
  non_solicitation_clause: z.boolean(),
  non_solicitation_months: z.number().optional(),

  // Governing Law & Dispute Resolution
  governing_law_country: z
    .string()
    .min(1, "Please specify the governing law country"),
  mediation_rules: z.string().min(1, "Please specify the mediation rules"),

  // User contact information (for external users)
  userFirstName: z.string().min(1, "Your first name is required"),
  userLastName: z.string().min(1, "Your last name is required"),
  userEmail: z.string().email("Please enter a valid email address"),
});

// Type inference from the schema
export type ContractorAgreementFormData = z.infer<typeof contractorFormSchema>;

// API request type
export interface GenerateContractorAgreementRequest {
  agreementData: ContractorAgreementFormData;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
}

// API response type
export interface GenerateContractorAgreementResponse {
  status: "success" | "error";
  message?: string;
  agreement_id?: string;
  download_url?: string;
  error?: string;
}

// API response type for creating Client contracts
export interface CreateContractorContractResponse {
  status: "success" | "error";
  message?: string;
  contract?: any; // ContractorContract object
  agreement_id?: string;
  pdf_path?: string;
  error?: string;
}

// Contractor contract interface for listing (this is what the API actually returns)
export interface ContractorContract {
  id: string; // UUID
  contractorEntityId: number;
  clientId: number | null;
  status:
    | "DRAFT"
    | "PENDING_CLIENT_APPROVAL"
    | "ACTIVE"
    | "COMPLETED"
    | "TERMINATED"
    | "CANCELLED"
    | "SUSPENDED";
  contractTitle: string;
  contractDescription: string;
  startDate: string | null;
  endDate: string | null;
  contractTerms: ContractorAgreementFormData; // The agreement data is stored here
  clientInvitationSentAt: string | null;
  clientInvitationEmail: string | null;
  contractorSignedAt: string | null;
  clientSignedAt: string | null;
  terminationDate: string | null;
  terminationReason: string | null;
  notes: string | null;
  contractorAgreementId: string; // UUID
  version: number;
  createdAt: string;
  createdById: number;
  isFullySigned: boolean;
  isActive: boolean;
  isPendingClient: boolean;
  canBeActivated: boolean;
}

// API response for listing Client contracts
export interface ListContractorContractsResponse {
  status: "success" | "error";
  message?: string;
  contracts: ContractorContract[];
  contractorEntityId: number;
  error?: string;
}

// Contractor agreement interface for listing
export interface ContractorAgreement {
  id: string; // UUID
  pdf_path: string;
  created_at: string;
  status: "draft" | "generated" | "sent" | "signed" | "archived";
  agreement_data: ContractorAgreementFormData;
  generated_by_user_email?: string;
  generated_by_user_first_name?: string;
  generated_by_user_last_name?: string;
  generated_by_employer_id?: number;
}

// API response for listing contractor agreements
export interface ListContractorAgreementsResponse {
  status: "success" | "error";
  contracts: ContractorAgreement[];
  error?: string;
}
